/**
 * @component NoticeDetail
 * @description
 * 공지사항 상세 화면
 * - 게시글 상세 내용 조회
 * - 첨부파일 다운로드
 * - 관련 링크 이동
 * - 이전 / 다음 게시글 이동
 * - 관리자 권한 시 수정 / 삭제 기능 제공
 * - 게시글 본문 내 이미지 클릭 시 이미지 뷰어 오픈
 *
 * @apiEndpoint
 * - noticeSlice.getContent : 게시글 상세 조회
 * - noticeSlice.deleteContent : 게시글 삭제
 * - noticeSlice.updateIMGContent : 이미지 업데이트 (에디터 이미지 처리)
 *
 * @route
 * - /notice/:noticeId
 *
 * @params
 * - noticeId : 조회할 게시글 ID
 *
 * @state
 * - showOptions : 관리자 옵션 메뉴 표시 여부
 * - isViewerOpen : 이미지 뷰어 열림 여부
 * - selectedImageIndex : 현재 선택된 이미지 index
 * - parsedContent : HTML 파싱 후 렌더링된 게시글 내용
 * - imgSrcList : 게시글 본문에 포함된 이미지 목록
 *
 * @reduxState
 * - content : 게시글 상세 정보
 * - files : 첨부파일 목록
 * - prev : 이전 게시글 정보
 * - next : 다음 게시글 정보
 * - admin : 관리자 로그인 여부
 * - deleteContentStatus : 게시글 삭제 상태
 *
 * @hooks
 * - useParams : URL에서 noticeId 추출
 * - useNavigate : 페이지 이동 처리
 * - useLocation : 이전 페이지 정보 확인
 * - useDispatch / useSelector : Redux 상태 관리
 *
 * @functions
 * - handleAction : 버튼 이벤트 통합 처리
 *   - TOGGLE_OPTIONS : 관리자 옵션 토글
 *   - PREV : 이전 게시글 이동
 *   - NEXT : 다음 게시글 이동
 *   - LIST : 게시글 목록 이동
 *   - UPDATE : 게시글 수정 페이지 이동
 *   - OPEN_IMAGE : 이미지 뷰어 오픈
 *   - CLOSE_IMAGE : 이미지 뷰어 닫기
 *
 * - handleDelete : 게시글 삭제 처리
 * - handleImageParse : 게시글 HTML 파싱 및 이미지 목록 추출
 *
 * @components
 * - ImgViewer : 이미지 확대 뷰어
 * - LazyImage : 게시글 이미지 Lazy Loading 컴포넌트
 * 
 * @since 
 * @author 개발팀
 * @history
 * - 2026-03-12 : 주석 추가, 코드 재배치, 각 버튼 핸들러들 handleAction으로 통합
 * - 2026-03-12 : handleImageParse, LazyImage 변경
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { getContent, deleteContent, resetDeleteContentStatus, updateIMGContent } from '../../features/notice/noticeSlice';
import { formatDateAndTime, formatDate } from '../../utils/formatDate';
import axiosInstance from '../../utils/api';
import prettyBytes from 'pretty-bytes';
import parse from "html-react-parser";
import ImgViewer from './imgViewer';
import { FadeLoader } from 'react-spinners';

const NoticeDetail = () => {
  //#region 1. State 정의
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { noticeId } = useParams();
  const [showOptions, setShowOptions] = useState(false);
  const content = useSelector((state) => state.notice.content);
  const files = useSelector((state) => state.notice.files);
  const prev = useSelector((state) => state.notice.prev);
  const next = useSelector((state) => state.notice.next);
  const admin = useSelector((state) => state.admin.admin);
  const apiBaseURL = axiosInstance.defaults.baseURL;
  const deleteContentStatus = useSelector((state) => state.notice.deleteContentStatus);

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const [parsedContent, setParsedContent] = useState(null);
  const [imgSrcList, setImgSrcList] = useState([]);
  //#endregion

  //#region 2. Refs 정의
  //#endregion

  //#region 3. Hooks (useEffect)
  useEffect(() => {
    if (location.hash === '#') {
      navigate(location.pathname, { replace: true });
      return;  // 해시 제거 후 경로 변경되므로 아래 fetchData 실행하지 않음
    }

    const fetchData = async () => {
      const actionResult = await dispatch(getContent({ noticeId }));

      if (getContent.rejected.match(actionResult)) {
        const errorCode = actionResult.payload;
        if (errorCode === 404) {
          navigate("/404", { replace: true });
        }
      }
    };

    fetchData();
  }, [dispatch, noticeId, navigate, location]);

  useEffect(() => {
    if (deleteContentStatus.success) {
      alert('삭제가 완료되었습니다.');
      dispatch(resetDeleteContentStatus());
      navigate('/notice');
    }
  }, [deleteContentStatus.success, navigate]);

  useEffect(() => {
    const { parsedContent, imgSrcList } = handleImageParse(content.content || '');
    setParsedContent(parsedContent);
    setImgSrcList(imgSrcList);
  }, [content]);

  useEffect(() => {
    if (isViewerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  
    return () => {
      document.body.style.overflow = "";
    };
  }, [isViewerOpen]);
  //#endregion

  //#region 4. Event Handlers
  const handleAction = (type, payload) => {
    switch (type) {
      case "TOGGLE_OPTIONS":
        setShowOptions(prev => !prev);
        break;
  
      case "PREV":
        if (prev) navigate(`/notice/${prev.id}`);
        break;
  
      case "NEXT":
        if (next) navigate(`/notice/${next.id}`);
        break;
  
      case "LIST":
        navigate("/notice");
        break;
  
      case "UPDATE":
        navigate(`/notice/content/${payload}`, {
          state: { from: location.pathname }
        });
        break;
  
      case "OPEN_IMAGE":
        console.log('@@')
        setSelectedImageIndex(payload);
        setIsViewerOpen(true);
        break;
  
      case "CLOSE_IMAGE":
        setIsViewerOpen(false);
        break;
  
      default:
        break;
    }
  };
  //#endregion

  //#region 5. Functions (호출되는 순서대로)
  /**
  * @description 게시글 삭제
  */
  const handleDelete = (e) => {
    e.preventDefault();
    if (window.confirm('정말 삭제하시겠습니까?')) {
      const actionResult = dispatch(deleteContent({ noticeId }));
      if (deleteContent.rejected.match(actionResult)) {
        const resultStatus = actionResult.payload;
        if (resultStatus !== 200) {
          alert('삭제 오류 발생');
        }
      }
    }
  };

  /**
   * @function handleImageParse
   * @description
   * 게시글 본문 HTML을 파싱하여 <img> 태그를 LazyImage 컴포넌트로 변환하는 함수.
   * TinyMCE 에디터에서 저장된 HTML 문자열을 React 요소로 변환하면서 이미지 처리 및 이미지 목록 추출을 동시에 수행한다.
   *
   * 주요 기능
   * - TinyMCE에서 저장된 escape 문자열(\" , &quot;) 정리
   * - html-react-parser를 사용하여 HTML을 React 요소로 변환
   * - <img> 태그를 LazyImage 컴포넌트로 교체
   * - 이미지 src를 배열에 저장하여 ImgViewer에서 사용
   * - 이미지 width / height / style 정보를 LazyImage로 전달
   * - 텍스트 노드는 React key 충돌 방지를 위해 span으로 감싸 반환
   *
   * 처리 과정
   * 1. escape된 HTML 문자열 정리
   * 2. HTML을 React DOM으로 파싱
   * 3. img 태그 발견 시 LazyImage 컴포넌트로 치환
   * 4. 이미지 src를 imgSrcList 배열에 저장
   *
   * @param {string} contentHtml 게시글 본문 HTML 문자열
   * @returns {{
   *   parsedContent: React.ReactNode,
   *   imgSrcList: string[]
   * }} 파싱된 React 콘텐츠와 이미지 src 목록
  */
  const handleImageParse = (contentHtml) => {
    const tempImgSrcList = [];
  
    const cleanHtml = contentHtml
      .replace(/\\"/g, '"')
      .replace(/\\&quot;/g, '"')
      .replace(/&quot;/g, '"');
  
    const parsed = parse(cleanHtml, {
      replace: (domNode) => {
  
        if (domNode.name === "img" && domNode.attribs?.src) {
  
          const imgIndex = tempImgSrcList.length;
  
          let src = domNode.attribs.src.replace(/^"+|"+$/g, "");

          // .net 배포시 주석 해제
          if (!src.startsWith("http://") && !src.startsWith("https://")) {
            const cleanPath = src.replace(/^(\.\.\/)+/, "/");
            src = `${process.env.REACT_APP_IMAGE_BASE_URL}${cleanPath}`;
          }
          
          tempImgSrcList.push(src);
  
          const width = parseInt(domNode.attribs.width || "0", 10);
          const height = parseInt(domNode.attribs.height || "0", 10);
  
          const style = domNode.attribs.style || "";
  
          return (
            <LazyImage
              key={`img-${imgIndex}`}
              src={src}
              alt={domNode.attribs.alt || ""}
              index={imgIndex}
              width={width}
              height={height}
              styleString={style}
              onOpen={handleAction}
            />
          );
        }
  
        if (domNode.type === "text") {
          return <span key={`txt-${Math.random()}`}>{domNode.data}</span>;
        }
      }
    });
  
    return { parsedContent: parsed, imgSrcList: tempImgSrcList };
  };
  //#endregion
    
  //#region 6. Render Functions
  /**
   * @function parseStyle
   * @description
   * TinyMCE 에디터에서 전달되는 inline style 문자열을 React에서 사용할 수 있는 style 객체로 변환하는 함수.
   * TinyMCE HTML 예 : style="display: block; margin-left: auto; margin-right: auto;"
   *
   * React에서는 style을 객체 형태로 사용해야 하므로 이를 camelCase 형태의 style 객체로 변환한다.
   * 변환 예 : "margin-left: auto" → { marginLeft: "auto" }
   *
   * @param {string} styleString TinyMCE에서 전달된 style 문자열
   * @returns {Object} React style 객체
   */
  const parseStyle = (styleString = "") => {
    const styleObj = {};
  
    styleString.split(";").forEach(rule => {
      const [key, value] = rule.split(":");
  
      if (!key || !value) return;
  
      const camelKey = key
        .trim()
        .replace(/-([a-z])/g, (_, char) => char.toUpperCase());
  
      styleObj[camelKey] = value.trim();
    });
  
    return styleObj;
  };

  /**
   * @component LazyImage
   * @description
   * 게시글 본문에 포함된 이미지를 렌더링하는 컴포넌트
   * - 이미지 로딩 전 FadeLoader 스피너 표시 (Lazy Loading)
   * - 이미지 로딩 완료 시 실제 이미지 표시
   * - TinyMCE 에디터에서 저장된 style(display, margin, float 등) 유지
   * - 원본 width / height 기준으로 이미지 크기 적용
   * - 화면 또는 컨테이너 너비가 이미지보다 작아지면 반응형 적용 (width:100%, height:auto)
   * - 이미지 클릭 시 ImgViewer를 열어 원본 이미지를 확인 가능
   *
   * @param {string} src 이미지 URL
   * @param {string} alt 이미지 설명
   * @param {number} index 이미지 index (ImgViewer에서 사용)
   * @param {number|string} width 이미지 원본 width
   * @param {number|string} height 이미지 원본 height
   * @param {string} styleString TinyMCE 에디터에서 전달된 inline style 문자열
   * @param {function} onOpen 이미지 클릭 시 실행되는 핸들러 (OPEN_IMAGE)
   */
  const LazyImage = ({ src, alt, index, width, height, styleString, onOpen}) => {

    const [loaded, setLoaded] = useState(false);
    const [responsiveStyle, setResponsiveStyle] = useState({});

    const originalWidth = width || 0;
    const originalHeight = height || 0;

    useEffect(() => {

      const updateSize = () => {
    
        const containerWidth = document.getElementById("bo_v_con")?.clientWidth || window.innerWidth;
        
        if (originalWidth > containerWidth) {
          setResponsiveStyle({ width: "100%", height: "auto" });
        } else {
          setResponsiveStyle({
            width: originalWidth ? `${originalWidth}px` : "100%",
            height: originalHeight ? `${originalHeight}px` : "auto",
          });
        }
      };
    
      updateSize();
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    
    }, [originalWidth, originalHeight, styleString]);


    const handleImageLoad = (e) => {
      if (e.target.complete && e.target.naturalWidth !== 0) { setLoaded(true); }
    };

    const tinyStyle = parseStyle(styleString);  

    return (
      <React.Fragment>
        {!loaded && (
          <FadeLoader
            color="#9a9a9a"
            height={10}
            width={4}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none"
            }}
          />
        )}
        <img
          src={src}
          alt={alt || `image-${index}`}
          onLoad={handleImageLoad}
          onClick={() => onOpen("OPEN_IMAGE", index)}
          style={{
            cursor: "pointer",
            visibility: loaded ? "visible" : "hidden",
            ...tinyStyle,
            ...responsiveStyle
          }}
        />
      </React.Fragment>
    );
  };
  //#endregion

  //#region 7. Main Render (UI)
  return (
    <div className={`flex-1 ${isViewerOpen ? "viewer-open" : ""}`}>
      <div className="bo_inner">
        <article id="bo_v">
          <header>
            <h2 id="bo_v_title">
              <span className="bo_v_tit">{content.title}</span>
            </h2>
          </header>
          <div id="bo_v_info">
            <div className="profile_info">
              <div className="profile_info_ct">
                <div className="bo_info">
                  <span>등록일</span>
                  <span className="reg_date">{formatDateAndTime(content.created_at)}</span>
                </div>
              </div>
            </div>
            {admin && (
              <div id="bo_v_top">
                <button
                  type="button"
                  className="btn_more_opt is_view_btn btn_b01 btn"
                  onClick={() => handleAction("TOGGLE_OPTIONS")}
                >
                  <i className="fa fa-ellipsis-v" aria-hidden="true"></i>
                  <span className="sound_only">옵션</span>
                </button>
                {showOptions && (
                  <ul className="more_opt is_view_btn">
                    <li>
                      <a onClick={() => handleAction("UPDATE", noticeId)}>
                        수정{content.id} <i className="edit-icon" aria-hidden="true"></i>
                      </a>
                    </li>
                    <li>
                      <a onClick={handleDelete}>
                        삭제<i className="delete-icon" aria-hidden="true"></i>
                      </a>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>

          <div id="bo_v_atc">
            <h2 id="bo_v_atc_title">본문</h2>
            <div id="bo_v_con">
              {parsedContent || <p>내용이 없습니다.</p>}
            </div>
          </div>

          {files && files.length > 0 && (
            <div id="bo_v_file">
              <h3>첨부파일</h3>
              <ul>
                {files.map((file, index) => (
                  <li key={index}>
                    <a href={apiBaseURL + '/file/download?fileId=' + file.id} className="view_file_download" download>
                      <i className="xi-file-text-o xi-x">{file.name} {
                      file.size && (
                        '(' + prettyBytes(file.size, { maximumFractionDigits: 2, locale: 'ko' }) + ')'
                      )}</i>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {content.link && content.link.length > 0 && (
            <div id="bo_v_link">
              <h3>관련링크</h3>
              <ul>
                <li>
                  <a
                    href={content.link.startsWith('http://') || content.link.startsWith('https://')
                      ? content.link
                      : `http://${content.link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="xi-link"></i> {content.link}
                  </a>
                </li>
              </ul>
            </div>
          )}

          <div className="btn_group">
            <a onClick={() => handleAction("LIST")} className="btn_list" title="목록">목록</a>
          </div>

          <div className="bo_v_nb">
            {next && (
              <div className="btn_next">
                <a onClick={() => handleAction("NEXT")}>
                  <span className="nb_tit"><i className="fa fa-chevron-up" aria-hidden="true">next</i></span>
                  {next.title}
                  <span className="nb_date"> {formatDate(next.created_at)}</span>
                </a>
              </div>
            )}
            {prev && (
              <div className="btn_prv">
                <a  onClick={() => handleAction("PREV")}>
                  <span className="nb_tit"><i className="fa fa-chevron-down" aria-hidden="true">prev</i></span>
                  {prev.title}
                  <span className="nb_date"> {formatDate(prev.created_at)}</span>
                </a>
              </div>
            )}
          </div>
        </article>
      </div>

      {isViewerOpen && (
        <ImgViewer
          title={content.title}
          images={imgSrcList}
          initialIndex={selectedImageIndex}
          closeViewer={() => handleAction("CLOSE_IMAGE")}
        />
      )}
    </div>
  );
  //#endregion
};

export default NoticeDetail;
